import type { Vec2 } from './types'
import type { Organism } from './organism'

export class SpatialGrid {
  private cellSize: number
  private cols: number
  private rows: number
  private cells: Map<number, Organism[]>
  private worldW: number
  private worldH: number

  constructor(worldWidth: number, worldHeight: number, cellSize: number) {
    this.worldW = worldWidth
    this.worldH = worldHeight
    this.cellSize = cellSize
    this.cols = Math.ceil(worldWidth / cellSize)
    this.rows = Math.ceil(worldHeight / cellSize)
    this.cells = new Map()
  }

  rebuild(organisms: Organism[]): void {
    this.cells.clear()
    for (const org of organisms) {
      if (!org.alive) continue
      const key = this.cellKey(org.pos.x, org.pos.y)
      let bucket = this.cells.get(key)
      if (!bucket) {
        bucket = []
        this.cells.set(key, bucket)
      }
      bucket.push(org)
    }
  }

  queryRadius(pos: Vec2, radius: number, excludeId?: number): Organism[] {
    const results: Organism[] = []
    const r2 = radius * radius

    const minCol = Math.floor((pos.x - radius) / this.cellSize)
    const maxCol = Math.floor((pos.x + radius) / this.cellSize)
    const minRow = Math.floor((pos.y - radius) / this.cellSize)
    const maxRow = Math.floor((pos.y + radius) / this.cellSize)

    for (let row = minRow; row <= maxRow; row++) {
      for (let col = minCol; col <= maxCol; col++) {
        // toroidal wrap
        const wCol = ((col % this.cols) + this.cols) % this.cols
        const wRow = ((row % this.rows) + this.rows) % this.rows
        const key = wCol + wRow * this.cols
        const bucket = this.cells.get(key)
        if (!bucket) continue

        for (const org of bucket) {
          if (excludeId !== undefined && org.id === excludeId) continue
          if (!org.alive) continue
          const d2 = this.toroidalDist2(pos, org.pos)
          if (d2 < r2) {
            results.push(org)
          }
        }
      }
    }
    return results
  }

  private cellKey(x: number, y: number): number {
    const col = Math.floor(x / this.cellSize)
    const row = Math.floor(y / this.cellSize)
    const wCol = ((col % this.cols) + this.cols) % this.cols
    const wRow = ((row % this.rows) + this.rows) % this.rows
    return wCol + wRow * this.cols
  }

  private toroidalDist2(a: Vec2, b: Vec2): number {
    let dx = Math.abs(a.x - b.x)
    let dy = Math.abs(a.y - b.y)
    if (dx > this.worldW / 2) dx = this.worldW - dx
    if (dy > this.worldH / 2) dy = this.worldH - dy
    return dx * dx + dy * dy
  }
}
